// ============== INTERNAL LINKING COMPONENT ==============
// Reusable component for keyword-rich internal links across all page types

import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, MapPin } from 'lucide-react';

// Full service list for internal linking
const linkableServices = [
  { id: 'hr', path: '/services/hr', anchor: 'HR outsourcing services', desc: 'Onboarding, payroll, and employee records management' },
  { id: 'finance', path: '/services/finance', anchor: 'finance & accounting BPO', desc: 'AP, AR, reconciliations, and month-end close support' },
  { id: 'operations', path: '/services/operations', anchor: 'business operations outsourcing', desc: 'Back-office processing, workflow coordination, and admin' },
  { id: 'customer-support', path: '/services/customer-support', anchor: 'offshore customer support', desc: 'Phone, email, chat, and ticket-based support teams' },
  { id: 'recruitment', path: '/services/recruitment', anchor: 'recruitment process outsourcing', desc: 'Sourcing, screening, scheduling, and ATS management' },
  { id: 'sales-support', path: '/services/sales-support', anchor: 'outsourced sales support', desc: 'Lead generation, CRM management, and pipeline admin' },
  { id: 'executive-assistants', path: '/services/executive-assistants', anchor: 'virtual executive assistant services', desc: 'Calendar, email, travel, and admin support' },
  { id: 'data-services', path: '/services/data-services', anchor: 'offshore data entry & processing', desc: 'Data entry, cleansing, enrichment, and reporting' },
  { id: 'fpa', path: '/services/fpa', anchor: 'FP&A outsourcing support', desc: 'Budgeting, forecasting, and management reporting' },
  { id: 'compliance-support', path: '/services/compliance-support', anchor: 'compliance BPO services', desc: 'Documentation, tracking, and audit preparation' },
  { id: 'project-management', path: '/services/project-management', anchor: 'project management outsourcing', desc: 'Coordination, status reporting, and scheduling' },
  { id: 'process-optimization', path: '/services/process-optimization', anchor: 'process optimization consulting', desc: 'Workflow analysis, documentation, and improvement' },
];

// Location pool for internal linking
const linkableLocations = [
  { id: 'new-york', name: 'New York' },
  { id: 'los-angeles', name: 'Los Angeles' },
  { id: 'chicago', name: 'Chicago' },
  { id: 'houston', name: 'Houston' },
  { id: 'san-francisco', name: 'San Francisco' },
  { id: 'seattle', name: 'Seattle' },
  { id: 'dallas', name: 'Dallas' },
  { id: 'miami', name: 'Miami' },
  { id: 'boston', name: 'Boston' },
  { id: 'atlanta', name: 'Atlanta' },
  { id: 'denver', name: 'Denver' },
  { id: 'austin', name: 'Austin' },
  { id: 'sydney', name: 'Sydney' },
  { id: 'melbourne', name: 'Melbourne' },
  { id: 'brisbane', name: 'Brisbane' },
  { id: 'perth', name: 'Perth' },
];

// Deterministic pick based on page seed
const pickItems = (arr, seed, count) => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
  const idx = Math.abs(hash);
  const result = [];
  const len = arr.length;
  for (let i = 0; i < count && i < len; i++) {
    result.push(arr[(idx + i * 7) % len]);
  }
  return result;
};

const InternalLinks = ({ currentPage = '', excludeServices = [], excludeLocations = [] }) => {
  const availableServices = linkableServices.filter(s => !excludeServices.includes(s.id));
  const availableLocations = linkableLocations.filter(l => !excludeLocations.includes(l.id));

  const linkedServices = pickItems(availableServices, currentPage, 3);
  const linkedLocations = pickItems(availableLocations, currentPage + '-loc', 3);

  return (
    <section className="section section-surface" data-testid="internal-links">
      <div className="container mx-auto">
        <h2 className="text-2xl font-bold text-[#0B1F3A] mb-6">Explore Our Outsourcing Services</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h3 className="font-semibold text-[#0B1F3A] mb-4">Related BPO Services</h3>
            <ul className="space-y-3">
              {linkedServices.map(s => (
                <li key={s.id}>
                  <Link
                    to={s.path}
                    className="text-[#2F80ED] hover:underline font-medium inline-flex items-center gap-1"
                    data-testid={`internal-link-service-${s.id}`}
                  >
                    <ChevronRight className="w-4 h-4 flex-shrink-0" />
                    {s.anchor}
                  </Link>
                  <span className="text-[#475569] text-sm ml-1">— {s.desc}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-[#0B1F3A] mb-4">Outsourcing by Location</h3>
            <ul className="space-y-3">
              {linkedLocations.map(l => (
                <li key={l.id}>
                  <Link
                    to={`/locations/${l.id}`}
                    className="text-[#2F80ED] hover:underline font-medium inline-flex items-center gap-1"
                    data-testid={`internal-link-location-${l.id}`}
                  >
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    {`outsourcing services in ${l.name}`}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InternalLinks;
